<script setup lang="ts" generic="Value">
import { computed, useAttrs, watchEffect } from 'vue';
import { type SelectFieldProps } from '../types';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import { getItemsForSelection, getPrefixedUuid, isSelectItemTypeObject } from '../utils';
import FormSelectionDisplay from '../display/FormSelectionDisplay.vue';
import { getSelectPassdownProps, getSelectionValue } from './utils';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<SelectFieldProps<Value>>(), {
  mode: 'edit',
  maxNumDisplayItems: 1000, // just a fictional high number
  autoSelect: false, // if there is only one option-item, select it
});

const isAutoselected = computed(() => props.autoSelect && props.items?.length === 1);

const passdownProps = computed(() => ({
  ...props,
  label: undefined,
  ...getSelectPassdownProps(props),
  readonly: isAutoselected.value,
}));

const id = getPrefixedUuid('select');

const attrs = useAttrs();
const passdownAttrs = computed(() => ({
  ...attrs,
  class: undefined,
}));

defineSlots<{
  append?(props: unknown): any;
  appendItem?(props: unknown): any;
  appendInner?(props: unknown): any;
}>();

const emits = defineEmits(['update:modelValue']);

function update(value: any) {
  emits('update:modelValue', getSelectionValue(value, props));
}

const selectedItems = computed(() => {
  return getItemsForSelection(passdownProps.value.items, props.modelValue, props.valueComparator);
});

function autoSelect() {
  if (isAutoselected.value) {
    const item = props.items?.[0];
    update(isSelectItemTypeObject(item) ? item.value : item);
  }
}

watchEffect(autoSelect);
</script>

<template>
  <div :class="$attrs.class">
    <FormLabel :label="label" :disabled="props.disabled" :for="id">
      <template #default="{ onBlur, onFocus, hoverProps }">
        <FormSelectionDisplay
          v-if="mode === 'details'"
          :value="selectedItems"
        ></FormSelectionDisplay>
        <v-select
          v-else
          v-bind="{ ...passdownProps, ...passdownAttrs, ...hoverProps, id } as any"
          :error="Boolean(errorMessage)"
          :hint="errorMessage || hint"
          :persistentHint="Boolean(errorMessage) || persistentHint"
          bgColor="surface"
          :label="label"
          :persistentPlaceholder="true"
          :no-data-text="$t('NoData')"
          @update:modelValue="update"
          @focus="onFocus()"
          @blur="onBlur()"
        >
          <template #selection="{ internalItem: item, index }">
            <template v-if="index < maxNumDisplayItems">
              {{ item.title }}
            </template>
            <template
              v-if="
                index < maxNumDisplayItems - 1 && Array.isArray(modelValue) && modelValue.length > 1
              "
              >,</template
            >
            <template v-else-if="index === maxNumDisplayItems && Array.isArray(modelValue)"
              >({{
                $t('OtherWithAmount', {
                  count: modelValue.length - maxNumDisplayItems,
                })
              }})</template
            >
          </template>

          <template v-if="chips" #chip="{ internalItem: item, index }">
            <v-chip v-if="index < maxNumDisplayItems">{{ item.title }}</v-chip>
            <span
              v-if="index === maxNumDisplayItems && Array.isArray(modelValue)"
              class="ml-4 text-caption"
            >
              {{
                $t('OtherWithAmount', {
                  count: modelValue.length - maxNumDisplayItems,
                })
              }}
            </span>
          </template>

          <template v-if="$slots.appendItem" #append-item>
            <slot name="appendItem" />
          </template>
          <template v-if="$slots.append" #append>
            <slot name="append" />
          </template>
        </v-select>
      </template>
    </FormLabel>
  </div>
</template>

<style scoped lang="scss">
:deep(.v-label) {
  display: none;
}

:deep(input) {
  top: 0;
  height: 100%;
}

:deep(.v-select.v-input--readonly) {
  .v-field__append-inner {
    .v-icon {
      opacity: var(--v-disabled-opacity);
    }
  }
}
</style>
